import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useLocation, useHistory, Link } from 'react-router-dom';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'sonner';
import {
    Landmark,
    RefreshCw,
    Upload,
    Download,
    CheckCircle,
    AlertCircle,
    AlertTriangle,
    CalendarClock,
    FilePlus,
    TrendingUp,
    TrendingDown,
} from 'lucide-react';

import axios from '../../../services/AxiosInstance';
import { getApiUrl } from '../../../utils/ApiUtils';
import PageHeader from '../../layouts/PageHeader';
import { ListToolbar, FilterChips } from '../Common/ListToolbar';
import { EmptyState } from '../Common/EmptyState';
import { useListControls } from '../../../hooks/useListControls';
import SideSheet from '../Common/SideSheet';
import FactsPopover from '../Common/FactsPopover';
import { resolveRoleAccounts, roleSettingsPath } from '../../../services/api/chartRolesApi';
import SkatteverketImportModal from './SkatteverketImportModal';
import SkattekontoDetail, { hasSuggestedLink, tierFor } from './SkattekontoDetail';
import { brandSelectTheme } from '../Common/reactSelectTheme';
import { useFeatures } from '../../../context/FeatureContext';
import { useCapabilities } from '../../../context/CapabilityContext';
import SkatteverketQueueDialog from '../Banking/payments/SkatteverketQueueDialog';
import { getVerificationById } from '../../../services/MatchingService';
import { proposalsFromResponse } from '../../../services/BookingProposalService';
import { formatCurrency, formatDate } from '../Banking/bankFormat';

// The Skattekonto page (2026-07-27 rebuild, side sheet 2026-10-07) — same
// design language and vocabulary as Bankhändelser, adapted to the tax
// rail's simpler reality: a skattekonto row either NEEDS a booking (rules
// from the skattekonto pack prefill it) or its bokföring already EXISTS
// (kopplad/klar). Rows come in two ways and land in the same list: "Hämta
// från Skatteverket" (BankID at Skatteverket, then the backend reads the
// account) and CSV import, which stays for history older than the API
// reaches. A row opens in the same side sheet as a bank row, with the
// booking view inside it.

const FETCH_LIMIT = 500;
const PAGE_SIZE = 50;
const config = { withCredentials: true };

const FILTER_TODO = 'todo';
const FILTER_MATCHED = 'matched';
const FILTER_ALL = 'all';
const FILTER_DONE = 'done';
// DISJOINT piles, mirroring Bankhändelser: Att bokföra = needs a booking,
// Att koppla = bokföring exists (suggested — the user confirms), Klara =
// done. The engine never links inferred matches on this rail either.
const FILTERS = [
    { key: FILTER_TODO, label: 'Att bokföra' },
    { key: FILTER_MATCHED, label: 'Att koppla' },
    { key: FILTER_ALL, label: 'Alla' },
    { key: FILTER_DONE, label: 'Klara' },
];

const SORT_OPTIONS = [
    { value: 'date_desc', label: 'Senaste först' },
    { value: 'date_asc', label: 'Äldsta först' },
    { value: 'amount_desc', label: 'Största belopp' },
];

const DATE_RANGE_OPTIONS = [
    { value: 'all', label: 'Alla datum' },
    { value: 'm3', label: 'Senaste 3 mån' },
    { value: 'year', label: 'I år' },
    { value: 'custom', label: 'Anpassat…' },
];

// Why a fetch from Skatteverket did not complete — the backend sends one of
// these codes back on the redirect, never Skatteverket's own text.
const SYNC_FAILURE_REASONS = {
    not_authorized: 'Du saknar behörighet till företagets skattekonto hos Skatteverket. En firmatecknare eller ett ombud behöver logga in.',
    no_tax_account: 'Skatteverket hittar inget skattekonto för företagets organisationsnummer.',
    invalid_orgnr: 'Skatteverket godtar inte företagets organisationsnummer. Kontrollera det under Företagsuppgifter.',
    missing_orgnr: 'Företagets organisationsnummer saknas eller är ogiltigt. Kontrollera det under Företagsuppgifter.',
    service_closed: 'Skattekontot är stängt hos Skatteverket just nu. Försök igen senare.',
    rate_limited: 'Skatteverket tar inte emot fler anrop just nu. Försök igen om en stund.',
    invalid_state: 'Inloggningen hos Skatteverket hann gå ut. Försök igen.',
    wrong_user: 'Hämtningen startades av en annan användare. Försök igen.',
    wrong_huvudman: 'Du valde ett annat företag hos Skatteverket än det du är inloggad i här. Välj det här företaget när Skatteverket frågar vem du företräder.',
};
const SYNC_FAILURE_DEFAULT = 'Det gick inte att hämta skattekontot från Skatteverket. Försök igen.';

const isDone = (line) => line.reconciliation_status !== 'unmatched';

// The page's account chips — same pill as the bank account chips on
// Bankhändelser: one fact row, number + name + saldo, nothing else.
const chipStyle = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    border: '1px solid #e5e7eb', background: '#fff',
    borderRadius: 999, padding: '4px 12px',
    fontSize: 12, color: '#374151', fontVariantNumeric: 'tabular-nums',
};
const muted = { color: '#9ca3af' };

const formatDateTime = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return `${formatDate(d.toISOString().slice(0, 10))} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

function rowLabel(line) {
    if (hasSuggestedLink(line)) {
        const nr = `${line.suggested_verification_series || ''}${line.suggested_verification_number || ''}`;
        return {
            text: nr ? `Koppla till verifikat ${nr}` : 'Koppla till verifikat',
            badge: 'Förslag finns',
            badgeColor: 'var(--primary)',
        };
    }
    if (line.reconciliation_status === 'unmatched') {
        return { text: 'Behöver bokföras', badge: 'Att bokföra', badgeColor: '#b45309' };
    }
    if (line.matched_verification_id) {
        const nr = `${line.verification_series || ''}${line.verification_number || ''}`;
        return {
            text: nr ? `Kopplad till verifikat ${nr}` : 'Kopplad till verifikat',
            badge: 'Klar',
            badgeColor: '#10b981',
        };
    }
    return { text: 'Avstämd — inget verifikat behövdes', badge: 'Klar', badgeColor: '#10b981' };
}

function StatementRow({ line, isSelected, isPending, onSelect }) {
    const amount = parseFloat(line.amount);
    const isInflow = amount > 0;
    const label = rowLabel(line);
    return (
        <button
            type="button"
            onClick={onSelect}
            className="matching-list-item"
            data-skv-line={line.statement_id}
            style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                border: 'none',
                borderBottom: '1px solid #f3f4f6',
                borderLeft: isSelected ? '3px solid var(--primary)' : '3px solid transparent',
                background: isSelected ? 'var(--rgba-primary-1)' : 'transparent',
                padding: '10px 16px',
                cursor: 'pointer',
                opacity: isPending ? 0.5 : 1,
                transition: 'opacity 0.15s ease',
            }}
            aria-busy={isPending || undefined}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {isInflow
                    ? <TrendingUp size={14} style={{ color: 'var(--bs-success)', flexShrink: 0 }} />
                    : <TrendingDown size={14} style={{ color: '#dc2626', flexShrink: 0 }} />}
                <span style={{ fontWeight: 600, fontSize: '0.8125rem', color: '#111827' }}>
                    {formatDate(line.transaction_date)}
                </span>
                <span style={{ fontSize: '0.8125rem', color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                    {line.description || '(ingen beskrivning)'}
                </span>
                <span style={{ fontWeight: 600, fontSize: '0.8125rem', color: isInflow ? '#10b981' : '#dc2626', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                    {formatCurrency(amount)}
                </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2, paddingLeft: 22 }}>
                <span style={{ fontSize: '0.75rem', color: '#6b7280', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {label.text}
                    {isPending && <Spinner animation="border" size="sm" className="ms-2" style={{ width: 12, height: 12 }} />}
                </span>
                <span style={{ flexShrink: 0, fontSize: 11, fontWeight: 600, color: label.badgeColor, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <CheckCircle size={12} />
                    {label.badge}
                </span>
            </div>
        </button>
    );
}

export default function SkattekontoPage() {
    const { companyId } = useParams();
    const location = useLocation();
    const history = useHistory();

    const [statements, setStatements] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [error, setError] = useState(null);
    // The account as Skatteverket last reported it (saldo, OCR, kommande).
    const [skvStatus, setSkvStatus] = useState(null);
    const { features } = useFeatures();
    const { capabilities } = useCapabilities();
    const isAdmin = Boolean(capabilities?.isAdmin);
    const [showQueueDialog, setShowQueueDialog] = useState(false);
    const canQueuePayment = Boolean(features?.payment_files) && isAdmin;
    const [isStartingSync, setIsStartingSync] = useState(false);
    // FILTER_TODO is the default, so the clean /skattekonto URL still opens on
    // "Att bokföra" without carrying ?filter=todo around.
    const {
        search: searchText,
        setSearch: setSearchText,
        debouncedSearch,
        filter: activeFilter,
        setFilter: setActiveFilter,
        sort: sortKey,
        setSort: setSortKey,
    } = useListControls({ defaultFilter: FILTER_TODO, defaultSort: 'date_desc' });
    const [dateRange, setDateRange] = useState('all');
    const [customFrom, setCustomFrom] = useState('');
    const [customTo, setCustomTo] = useState('');
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

    const [selectedId, setSelectedId] = useState(null);
    const [pendingId, setPendingId] = useState(null);
    // Sheet data for the selected row: the kontering proposals (unbooked
    // rows) or the linked / suggested verifikat.
    const [proposals, setProposals] = useState([]);
    const [proposalsLoading, setProposalsLoading] = useState(false);
    const [proposalsError, setProposalsError] = useState(null);
    const [linkedVerification, setLinkedVerification] = useState(null);
    const [actionBusy, setActionBusy] = useState(false);
    const [actionError, setActionError] = useState(null);
    // Inline booking view in the sheet: null, or { token, proposal?, manual? }.
    const [booking, setBooking] = useState(null);
    const fetchToken = useRef(0);

    const [showImportModal, setShowImportModal] = useState(false);

    // Account names for the proposal slips (the proposals carry numbers;
    // the standard kontering carries no names). Non-fatal.
    const [accounts, setAccounts] = useState([]);
    useEffect(() => {
        if (!companyId) return undefined;
        let active = true;
        axios.get(getApiUrl(`${companyId}/accounting/accounts?scope=company`), config)
            .then(({ data }) => { if (active) setAccounts(data?.accounts || []); })
            .catch(() => {});
        return () => { active = false; };
    }, [companyId]);
    const accountNameFor = useCallback((accountNumber) => (
        accounts.find((account) => String(account.account_number) === String(accountNumber))?.account_name || ''
    ), [accounts]);
    useEffect(() => {
        if (accounts.length === 0) return;
        setProposals((prev) => prev.map((proposal) => ({
            ...proposal,
            rows: proposal.rows.map((row) => (row.account_name ? row : { ...row, account_name: accountNameFor(row.account_number) })),
        })));
    }, [accounts, accountNameFor]);

    const load = useCallback(async () => {
        if (!companyId) return;
        try {
            const url = getApiUrl(`${companyId}/accounting/reconciliation/skatteverket/statements`);
            const { data } = await axios.get(url, { ...config, params: { limit: FETCH_LIMIT } });
            setStatements(data?.statements || []);
            setError(null);
        } catch (err) {
            setError(err?.response?.data?.message || err.message || 'Kunde inte hämta skattekontotransaktioner');
        } finally {
            // Non-fatal: the list works without the Skatteverket snapshot.
            axios.get(getApiUrl(`${companyId}/accounting/reconciliation/skatteverket/sync/status`), config)
                .then(({ data }) => setSkvStatus(data || null))
                .catch(() => setSkvStatus(null));
            setIsLoading(false);
            setIsRefreshing(false);
        }
    }, [companyId]);

    useEffect(() => { setIsLoading(true); load(); }, [load]);

    // ── Sheet data: kontering proposals for unbooked rows, linked verifikat
    //    for matched rows. Same deferred-open pattern as Bankhändelser:
    //    the clicked row dims until data is ready. ──
    useEffect(() => {
        if (!pendingId) return;
        const token = ++fetchToken.current;
        setProposals([]);
        setProposalsError(null);
        setLinkedVerification(null);
        setActionError(null);

        const line = statements.find((l) => l.statement_id === pendingId);
        const done = () => {
            if (fetchToken.current !== token) return;
            setPendingId(null);
            setSelectedId(pendingId);
        };

        if (line && hasSuggestedLink(line)) {
            getVerificationById(companyId, line.suggested_verification_id)
                .then((data) => {
                    if (fetchToken.current !== token) return;
                    setLinkedVerification(data?.verification || data || null);
                })
                .catch(() => {})
                .finally(done);
        } else if (line && line.reconciliation_status === 'unmatched') {
            // The ladder for this rail: the rule pack's kontering first, then
            // the standard kontering for the transaction type — already in
            // the unified proposal shape from the backend.
            setProposalsLoading(true);
            const url = getApiUrl(`${companyId}/accounting/reconciliation/skatteverket/statements/${pendingId}/verification-suggestions`);
            axios.get(url, config)
                .then(({ data }) => {
                    if (fetchToken.current !== token) return;
                    setProposals(proposalsFromResponse(data, {
                        transaction: line,
                        defaultLegAccount: { number: '', name: '' },
                        source: 'tax_authority',
                        accountNameFor,
                    }));
                })
                .catch(() => {
                    if (fetchToken.current === token) setProposalsError('Kunde inte hämta förslag');
                })
                .finally(() => {
                    if (fetchToken.current === token) setProposalsLoading(false);
                    done();
                });
        } else if (line && line.matched_verification_id) {
            getVerificationById(companyId, line.matched_verification_id)
                .then((data) => {
                    if (fetchToken.current !== token) return;
                    setLinkedVerification(data?.verification || data || null);
                })
                .catch(() => {})
                .finally(done);
        } else {
            done();
        }
        // accountNameFor is read at call time on purpose; a later accounts
        // arrival fills the names in above.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, pendingId, statements]);

    const handleSelectRow = useCallback((line) => {
        if (line.statement_id === selectedId || line.statement_id === pendingId) return;
        // A row switch leaves any open draft behind.
        setBooking(null);
        setPendingId(line.statement_id);
        // Sheet already open → swap it to the new row now (header renders
        // from list data, body shows loading). Sheet closed → stays closed
        // until data is ready.
        if (selectedId) setSelectedId(line.statement_id);
    }, [selectedId, pendingId]);

    const closeDetail = useCallback(() => {
        if (booking) setBooking(null);
        else setSelectedId(null);
    }, [booking]);

    // ── Filtering pipeline (date → pills → search → sort → window) ──
    const dateBounds = useMemo(() => {
        const iso = (d) => d.toISOString().slice(0, 10);
        const today = new Date();
        switch (dateRange) {
            case 'm3': { const d = new Date(today); d.setMonth(d.getMonth() - 3); return { from: iso(d), to: null }; }
            case 'year': return { from: `${today.getFullYear()}-01-01`, to: null };
            case 'custom': return { from: customFrom || null, to: customTo || null };
            default: return { from: null, to: null };
        }
    }, [dateRange, customFrom, customTo]);

    const dateFiltered = useMemo(() => statements.filter((l) => {
        if (!dateBounds.from && !dateBounds.to) return true;
        const d = String(l.transaction_date || '').slice(0, 10);
        if (dateBounds.from && d < dateBounds.from) return false;
        if (dateBounds.to && d > dateBounds.to) return false;
        return true;
    }), [statements, dateBounds]);

    const filterGroups = useMemo(() => {
        const groups = { [FILTER_TODO]: [], [FILTER_MATCHED]: [], [FILTER_ALL]: dateFiltered, [FILTER_DONE]: [] };
        for (const line of dateFiltered) {
            if (hasSuggestedLink(line)) groups[FILTER_MATCHED].push(line);
            else if (isDone(line)) groups[FILTER_DONE].push(line);
            else groups[FILTER_TODO].push(line);
        }
        return groups;
    }, [dateFiltered]);

    const visibleLines = useMemo(() => {
        const lines = filterGroups[activeFilter] || [];
        const needle = debouncedSearch.toLowerCase();
        const filtered = !needle ? lines : lines.filter((l) =>
            (l.description || '').toLowerCase().includes(needle)
            || String(l.amount || '').includes(needle));
        const sorted = [...filtered];
        const dateOf = (l) => String(l.transaction_date || '');
        switch (sortKey) {
            case 'date_asc': sorted.sort((a, b) => dateOf(a).localeCompare(dateOf(b))); break;
            case 'amount_desc': sorted.sort((a, b) => Math.abs(parseFloat(b.amount)) - Math.abs(parseFloat(a.amount))); break;
            default: sorted.sort((a, b) => dateOf(b).localeCompare(dateOf(a)));
        }
        return sorted;
    }, [filterGroups, activeFilter, debouncedSearch, sortKey]);

    useEffect(() => { setVisibleCount(PAGE_SIZE); }, [activeFilter, debouncedSearch, sortKey, dateRange, customFrom, customTo]);
    const windowedLines = useMemo(() => visibleLines.slice(0, visibleCount), [visibleLines, visibleCount]);

    const selectedLine = useMemo(
        () => visibleLines.find((l) => l.statement_id === selectedId) || null,
        [visibleLines, selectedId],
    );
    const selectedIndex = selectedLine ? windowedLines.findIndex((l) => l.statement_id === selectedLine.statement_id) : -1;
    const stepSelection = useCallback((delta) => {
        const next = selectedIndex >= 0 ? windowedLines[selectedIndex + delta] : null;
        if (!next) return;
        handleSelectRow(next);
        const rowEl = document.querySelector(`[data-skv-line="${next.statement_id}"]`);
        if (rowEl && rowEl.scrollIntoView) rowEl.scrollIntoView({ block: 'nearest' });
    }, [selectedIndex, windowedLines, handleSelectRow]);

    useEffect(() => {
        // No row switching under an open draft.
        if (!selectedLine || booking) return undefined;
        const onKeyDown = (e) => {
            if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
            if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
            if (document.body.classList.contains('modal-open')) return;
            const el = e.target;
            if (el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))) return;
            e.preventDefault();
            stepSelection(e.key === 'ArrowDown' ? 1 : -1);
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [selectedLine, booking, stepSelection]);

    // After an action on the selected row: keep the user in the flow by
    // moving to the next row of the same pile (or the previous one at the
    // end), then refetch so the list reflects the change.
    const handleMutated = useCallback(() => {
        const next = selectedIndex >= 0 ? (windowedLines[selectedIndex + 1] || windowedLines[selectedIndex - 1] || null) : null;
        setBooking(null);
        if (next) {
            handleSelectRow(next);
            const rowEl = document.querySelector(`[data-skv-line="${next.statement_id}"]`);
            if (rowEl && rowEl.scrollIntoView) rowEl.scrollIntoView({ block: 'nearest' });
        } else {
            setSelectedId(null);
        }
        setIsRefreshing(true);
        load();
    }, [load, selectedIndex, windowedLines, handleSelectRow]);

    // Current skattekonto saldo = the newest balance-carrying row (full
    // set — the date filter never changes a point-in-time fact).
    const currentSaldo = useMemo(() => {
        let latest = null;
        for (const line of statements) {
            if (line.balance == null || line.balance === '') continue;
            const d = String(line.transaction_date || '');
            if (!latest || d > latest.date) latest = { date: d, balance: parseFloat(line.balance) };
        }
        return latest;
    }, [statements]);

    const todoCount = (filterGroups[FILTER_TODO] || []).length;
    const doneCount = (filterGroups[FILTER_DONE] || []).length;

    // ── Actions ──
    // Koppla (single): the user's confirm of a suggested link. Link-only —
    // runs on the click, errors inline in the sheet, no toast.
    const handleKoppla = useCallback(async () => {
        if (!selectedLine) return;
        setActionBusy(true);
        setActionError(null);
        try {
            const url = getApiUrl(`${companyId}/accounting/reconciliation/skatteverket/statements/${selectedLine.statement_id}/confirm-link`);
            await axios.post(url, {}, config);
            handleMutated();
        } catch (err) {
            setActionError(err?.response?.data?.message || 'Kunde inte koppla raden');
        } finally {
            setActionBusy(false);
        }
    }, [companyId, selectedLine, handleMutated]);

    // Koppla alla — same visible per-row cascade as Bankhändelser.
    const [linkAllBusy, setLinkAllBusy] = useState(false);
    const [linkProgress, setLinkProgress] = useState(null);
    const [processingId, setProcessingId] = useState(null);
    const [leavingIds, setLeavingIds] = useState(() => new Set());
    // Rows the last run could not link: said once, inline, where the
    // button is — the linked rows have already left the pile visibly.
    const [linkAllSkipped, setLinkAllSkipped] = useState(0);
    const handleKopplaAlla = useCallback(async () => {
        const lines = [...(filterGroups[FILTER_MATCHED] || [])];
        if (lines.length === 0) return;
        setLinkAllBusy(true);
        setLinkAllSkipped(0);
        setLinkProgress({ done: 0, total: lines.length });
        let skipped = 0;
        try {
            for (const line of lines) {
                setProcessingId(line.statement_id);
                try {
                    const url = getApiUrl(`${companyId}/accounting/reconciliation/skatteverket/statements/${line.statement_id}/confirm-link`);
                    await axios.post(url, {}, config);
                    setLeavingIds((prev) => new Set(prev).add(line.statement_id));
                    setTimeout(() => {
                        setStatements((prev) => prev.map((l) =>
                            l.statement_id === line.statement_id
                                ? { ...l, reconciliation_status: 'verified', matched_verification_id: l.suggested_verification_id, verification_series: l.suggested_verification_series, verification_number: l.suggested_verification_number, suggested_verification_id: null }
                                : l));
                        setLeavingIds((prev) => {
                            const next = new Set(prev);
                            next.delete(line.statement_id);
                            return next;
                        });
                    }, 300);
                } catch (rowError) {
                    skipped += 1;
                }
                setProcessingId(null);
                setLinkProgress((p) => ({ done: (p?.done || 0) + 1, total: p?.total || lines.length }));
            }
            setLinkAllSkipped(skipped);
            load();
        } finally {
            setLinkAllBusy(false);
            setLinkProgress(null);
            setProcessingId(null);
        }
    }, [companyId, filterGroups, load]);

    const handleUnlink = useCallback(async () => {
        if (!selectedLine) return;
        setActionBusy(true);
        setActionError(null);
        try {
            const url = getApiUrl(`${companyId}/accounting/reconciliation/skatteverket/statements/${selectedLine.statement_id}/match`);
            await axios.delete(url, config);
            handleMutated();
        } catch (err) {
            setActionError(err?.response?.data?.message || 'Kunde inte ta bort kopplingen');
        } finally {
            setActionBusy(false);
        }
    }, [companyId, selectedLine, handleMutated]);

    const openBooking = useCallback(({ proposal = null, manual = false } = {}) => {
        setActionError(null);
        setBooking({ token: Date.now(), proposal, manual });
    }, []);

    const handleBookingSuccess = useCallback(() => {
        toast.success('Verifikatet skapades.');
        handleMutated();
    }, [handleMutated]);

    const handleImportSuccess = useCallback(() => {
        setShowImportModal(false);
        toast.success('Skattekontotransaktionerna importerades — regler och matchning körs i bakgrunden.', { duration: 8000 });
        // The server-side post-import chain (auto-apply → matching → bank
        // re-run) works asynchronously; refresh shortly after.
        setTimeout(() => load(), 4000);
        handleMutated();
    }, [handleMutated, load]);

    // Landing after the round trip to Skatteverket. The param is consumed and
    // removed so a refresh doesn't re-toast.
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const status = params.get('skattekonto_sync');
        if (!status) return;

        if (status === 'success') {
            toast.success('Skattekontot hämtades från Skatteverket — regler och matchning körs i bakgrunden.', { duration: 8000 });
            // Same asynchronous post-import chain as a CSV import.
            setTimeout(() => load(), 4000);
        } else if (status === 'cancelled') {
            toast.info('Hämtningen avbröts.');
        } else {
            toast.error(SYNC_FAILURE_REASONS[params.get('reason')] || SYNC_FAILURE_DEFAULT, { duration: 10000 });
        }
        params.delete('skattekonto_sync');
        params.delete('reason');
        const rest = params.toString();
        history.replace(`${location.pathname}${rest ? `?${rest}` : ''}`);
        load();
    }, [location.search, location.pathname, history, load]);

    const handleFetchFromSkatteverket = useCallback(async () => {
        setIsStartingSync(true);
        try {
            const url = getApiUrl(`${companyId}/accounting/reconciliation/skatteverket/sync/authorize`);
            const { data } = await axios.post(url, {}, config);
            // Full-page redirect, same shape as the bank and Zettle flows.
            window.location.assign(data.authorizationUrl);
        } catch (err) {
            toast.error(SYNC_FAILURE_REASONS[err?.response?.data?.code] || SYNC_FAILURE_DEFAULT);
            setIsStartingSync(false);
        }
    }, [companyId]);

    const skvAccount = skvStatus?.account || null;

    // The konto the skattekonto is booked on: the tenant's tax_account chart
    // role, never a hardcoded number. Unresolved = an actionable state.
    const [roleLegs, setRoleLegs] = useState(null);
    useEffect(() => {
        if (!companyId) return undefined;
        let active = true;
        resolveRoleAccounts(companyId, ['tax_account']).then((legs) => { if (active) setRoleLegs(legs); });
        return () => { active = false; };
    }, [companyId]);
    const taxLeg = roleLegs?.tax_account
        ? { account_number: roleLegs.tax_account.accountNumber, account_name: roleLegs.tax_account.accountName }
        : null;

    // Saldo = Skatteverket's own figure from the last fetch, else the newest
    // balance-carrying row. Kommande = decided, not yet on the account.
    const saldo = skvAccount && skvAccount.balanceSkatteverket != null
        ? skvAccount.balanceSkatteverket
        : currentSaldo?.balance ?? null;
    const upcoming = skvAccount?.upcoming || [];
    const upcomingTotal = upcoming.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
    const kronofogdenBalance = Number(skvAccount?.balanceKronofogden) || 0;
    const nonZero = (value) => (Number(value) ? formatCurrency(Number(value)) : null);

    // Coming back from Skatteverket with the browser's back button restores
    // this page from the back/forward cache exactly as it was left — spinner
    // and all. The button is for clicking again, so put it back.
    useEffect(() => {
        const onPageShow = (event) => { if (event.persisted) setIsStartingSync(false); };
        window.addEventListener('pageshow', onPageShow);
        return () => window.removeEventListener('pageshow', onPageShow);
    }, []);

    return (
        <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <PageHeader
                    icon={Landmark}
                    title="Skattekonto"
                    subtitle="Importera, bokför och stäm av ditt skattekonto hos Skatteverket."
                    actions={
                        <>
                        <Button variant="outline-secondary" size="sm" onClick={() => { setIsRefreshing(true); load(); }} disabled={isRefreshing}>
                            <RefreshCw size={14} className={isRefreshing ? 'me-1 spin' : 'me-1'} />
                            Uppdatera
                        </Button>
                        <Button variant="outline-secondary" size="sm" onClick={() => setShowImportModal(true)}>
                            <Upload size={14} className="me-1" />
                            Importera fil
                        </Button>
                        <Button variant="primary" size="sm" onClick={handleFetchFromSkatteverket} disabled={isStartingSync}>
                            {isStartingSync
                                ? <Spinner animation="border" size="sm" className="me-1" style={{ width: 14, height: 14 }} />
                                : <Download size={14} className="me-1" />}
                            Hämta från Skatteverket
                        </Button>
                        {canQueuePayment && (
                            <Button variant="outline-primary" size="sm" onClick={() => setShowQueueDialog(true)} title="Betalning till skattekontot via betalfil (ISO 20022); OCR från senaste hämtningen">
                                <Landmark size={14} className="me-1" />
                                Lägg i betalningskö
                            </Button>
                        )}
                        </>
                    }
                />
                {canQueuePayment && (
                    <SkatteverketQueueDialog
                        show={showQueueDialog}
                        companyId={companyId}
                        onClose={() => setShowQueueDialog(false)}
                        onQueued={() => { setShowQueueDialog(false); history.push(`/${companyId}/payments`); }}
                    />
                )}

                {/* Summary strip */}
                {!isLoading && statements.length > 0 && (
                    <div className="matching-state-box" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 16, padding: '12px 16px' }}>
                        <span style={{ fontSize: 13, color: '#374151', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <CheckCircle size={15} style={{ color: '#10b981' }} />
                            <strong>{doneCount}</strong> klara
                        </span>
                        <span style={{ fontSize: 13, color: '#374151', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <FilePlus size={15} style={{ color: '#b45309' }} />
                            <strong>{todoCount}</strong> att bokföra
                        </span>
                    </div>
                )}

                {/* Account facts, the way Bankhändelser places its bank
                    accounts: one chip per account, the rest behind the
                    info icon. */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                    {roleLegs && !taxLeg ? (
                        <Link
                            to={roleSettingsPath(companyId)}
                            style={{ ...chipStyle, border: '1px solid #fcd34d', background: '#fffbeb', color: '#92400e', textDecoration: 'none' }}
                            title="Utan rollen Skattekonto kan raderna inte förifyllas — mappa rollen i kontoplanen"
                        >
                            <AlertTriangle size={12} />
                            Skattekonto
                            <span>saknar konto i kontoplanen — mappa rollen</span>
                        </Link>
                    ) : (
                        <span style={chipStyle}>
                            <Landmark size={12} style={{ color: '#6b7280' }} />
                            Skattekonto
                            {taxLeg && (
                                <>
                                    <span style={muted}>bokförs på</span>
                                    <strong>{taxLeg.account_number}</strong>
                                    <span style={{ color: '#6b7280' }}>{taxLeg.account_name}</span>
                                </>
                            )}
                            {saldo != null && (
                                <>
                                    <span style={muted}>·</span>
                                    <span style={muted}>Saldo</span>
                                    <strong>{formatCurrency(saldo)}</strong>
                                </>
                            )}
                            {skvAccount && (
                                <FactsPopover
                                    id="skattekonto-account-facts"
                                    label="Uppgifter från Skatteverket"
                                    facts={[
                                        ['Saldo hos Skatteverket', skvAccount.balanceSkatteverket != null ? formatCurrency(skvAccount.balanceSkatteverket) : null],
                                        ['Ränta', nonZero(skvAccount.interestSkatteverket)],
                                        ['OCR', skvAccount.ocrNumber],
                                        ['Nästa avstämning', skvAccount.nextReconciliationDate ? formatDate(skvAccount.nextReconciliationDate) : null],
                                        ['Uppdaterat hos Skatteverket', formatDateTime(skvAccount.providerUpdatedAt)],
                                        ['Hämtat', formatDateTime(skvAccount.fetchedAt)],
                                    ]}
                                />
                            )}
                        </span>
                    )}
                    {skvAccount?.ocrNumber && (
                        <span style={chipStyle} title="OCR-nummer för inbetalning till skattekontot">
                            <span style={muted}>OCR</span>
                            <strong>{skvAccount.ocrNumber}</strong>
                        </span>
                    )}
                    {upcoming.length > 0 && (
                        <span style={chipStyle}>
                            <CalendarClock size={12} style={{ color: '#6b7280' }} />
                            {upcoming.length} kommande
                            <span style={muted}>·</span>
                            <strong style={{ color: upcomingTotal < 0 ? '#dc2626' : '#10b981' }}>{formatCurrency(upcomingTotal)}</strong>
                            <FactsPopover id="skattekonto-upcoming" label="Kommande på skattekontot" facts={[]} min={0}>
                                <div style={{ fontSize: '0.75rem', color: '#374151' }}>
                                    {upcoming.map((item, index) => (
                                        <div key={`${item.date}-${index}`} style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: '2px 0' }}>
                                            <span style={{ fontWeight: 500, color: '#6b7280', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{formatDate(item.date)}</span>
                                            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.text}</span>
                                            {item.amount != null && (
                                                <span style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', color: item.amount < 0 ? '#dc2626' : '#10b981' }}>{formatCurrency(item.amount)}</span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </FactsPopover>
                        </span>
                    )}
                    {kronofogdenBalance !== 0 && (
                        <span style={{ ...chipStyle, border: '1px solid #fcd34d', background: '#fffbeb', color: '#92400e' }}>
                            <AlertTriangle size={12} />
                            Kronofogden
                            <span style={{ color: '#b45309' }}>Saldo</span>
                            <strong>{formatCurrency(kronofogdenBalance)}</strong>
                            <FactsPopover
                                id="skattekonto-kronofogden"
                                label="Uppgifter från Kronofogden"
                                facts={[
                                    ['Saldo hos Kronofogden', formatCurrency(kronofogdenBalance)],
                                    ['Ränta', nonZero(skvAccount.interestKronofogden)],
                                ]}
                            />
                        </span>
                    )}
                </div>

                <ListToolbar
                    search={{
                        value: searchText,
                        onChange: setSearchText,
                        placeholder: 'Sök beskrivning eller belopp…',
                        ariaLabel: 'Sök bland skattekontotransaktioner',
                    }}
                    sort={{ options: SORT_OPTIONS, value: sortKey, onChange: (v) => setSortKey(v || 'date_desc'), ariaLabel: 'Sortera' }}
                >
                    <div style={{ minWidth: 170 }}>
                        <Select
                            options={DATE_RANGE_OPTIONS}
                            value={DATE_RANGE_OPTIONS.find((o) => o.value === dateRange) || DATE_RANGE_OPTIONS[0]}
                            onChange={(opt) => setDateRange(opt?.value || 'all')}
                            isSearchable={false}
                            theme={brandSelectTheme}
                            styles={{ control: (b) => ({ ...b, minHeight: 38, fontSize: 13 }), option: (b) => ({ ...b, fontSize: 13 }) }}
                            aria-label="Filtrera på datum"
                        />
                    </div>
                    {dateRange === 'custom' && (
                        <>
                            <Form.Control type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} aria-label="Från datum" style={{ width: 150 }} />
                            <span style={{ color: '#9ca3af', fontSize: 13 }}>–</span>
                            <Form.Control type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} aria-label="Till datum" style={{ width: 150 }} />
                        </>
                    )}
                </ListToolbar>

                <FilterChips
                    chips={FILTERS.map((c) => ({ ...c, count: (filterGroups[c.key] || []).length }))}
                    value={activeFilter}
                    onChange={(key) => { setActiveFilter(key); setSelectedId(null); setLinkAllSkipped(0); }}
                />

                {activeFilter === FILTER_MATCHED && (filterGroups[FILTER_MATCHED] || []).length > 0 && (
                    <div
                        className="matching-state-box"
                        style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '10px 16px' }}
                    >
                        <span style={{ fontSize: 13, color: '#374151' }}>
                            Dessa rader har redan en bokföring — koppla ihop dem så är de klara.
                            Inget nytt bokförs och varje rad kan ångras.
                        </span>
                        {linkAllSkipped > 0 && !linkAllBusy && (
                            <span style={{ fontSize: 13, color: '#b45309', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <AlertCircle size={14} />
                                {linkAllSkipped} {linkAllSkipped === 1 ? 'rad kunde inte kopplas och ligger kvar' : 'rader kunde inte kopplas och ligger kvar'}.
                            </span>
                        )}
                        <Button
                            variant="success"
                            size="sm"
                            style={{ marginLeft: 'auto' }}
                            onClick={handleKopplaAlla}
                            disabled={linkAllBusy}
                        >
                            {linkAllBusy ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Kopplar… {linkProgress ? `${linkProgress.done}/${linkProgress.total}` : ''}
                                </>
                            ) : (
                                <>
                                    <CheckCircle size={14} className="me-1" />
                                    Koppla alla {(filterGroups[FILTER_MATCHED] || []).length}
                                </>
                            )}
                        </Button>
                    </div>
                )}

                {error && (
                    <Alert variant="danger" onClose={() => setError(null)} dismissible className="mb-0">
                        <AlertCircle size={14} className="me-2" />
                        {error}
                    </Alert>
                )}

                {/* The list. The selected row opens in the side sheet, which
                    slides in over the page like on Bankhändelser. */}
                <>
                    <div>
                        {isLoading ? (
                            <div className="matching-state-box" style={{ padding: 40, textAlign: 'center' }}>
                                <Spinner animation="border" size="sm" />
                                <span className="ms-2">Laddar transaktioner…</span>
                            </div>
                        ) : visibleLines.length === 0 ? (
                            statements.length === 0 ? (
                                <div className="matching-state-box" style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
                                    <Landmark size={32} style={{ opacity: 0.4 }} />
                                    <p style={{ marginTop: 12, marginBottom: 4, fontWeight: 500 }}>
                                        Hämta ditt skattekonto
                                    </p>
                                    <p style={{ fontSize: 13, marginBottom: 12 }}>
                                        Logga in hos Skatteverket med BankID så hämtas transaktionerna hit —
                                        reglerna bokför de vanliga händelserna åt dig.
                                    </p>
                                    <Button variant="primary" size="sm" onClick={handleFetchFromSkatteverket} disabled={isStartingSync}>
                                        <Download size={14} className="me-1" />
                                        Hämta från Skatteverket
                                    </Button>
                                </div>
                            ) : activeFilter === FILTER_TODO && !searchText ? (
                                <div className="matching-state-box" style={{ padding: 40, textAlign: 'center' }}>
                                    <CheckCircle size={32} style={{ color: '#10b981' }} />
                                    <p style={{ marginTop: 12, marginBottom: 4, fontWeight: 600, color: '#111827' }}>
                                        Allt är bokfört
                                    </p>
                                    <p style={{ fontSize: 13, marginBottom: 0, color: '#6b7280' }}>
                                        {doneCount > 0 ? `${doneCount} rader är klara.` : 'Importera fler transaktioner när du vill.'}
                                    </p>
                                </div>
                            ) : (
                                <EmptyState
                                    variant="no-matches"
                                    query={debouncedSearch}
                                    title={debouncedSearch ? 'Inga träffar' : 'Inga transaktioner i denna vy'}
                                    description="Byt filter för att se andra transaktioner."
                                />
                            )
                        ) : (
                            <div className="matching-list-box">
                                {windowedLines.map((line) => (
                                    <div
                                        key={line.statement_id}
                                        style={{
                                            transition: 'opacity 300ms ease, transform 300ms ease',
                                            opacity: leavingIds.has(line.statement_id) ? 0 : 1,
                                            transform: leavingIds.has(line.statement_id) ? 'translateX(16px)' : 'none',
                                        }}
                                    >
                                        <StatementRow
                                            line={line}
                                            isSelected={line.statement_id === selectedId}
                                            isPending={line.statement_id === pendingId || line.statement_id === processingId}
                                            onSelect={() => handleSelectRow(line)}
                                        />
                                    </div>
                                ))}
                                {visibleLines.length > windowedLines.length && (
                                    <div style={{ padding: 12, textAlign: 'center', borderTop: '1px solid #f3f4f6' }}>
                                        <Button variant="outline-secondary" size="sm" onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}>
                                            Visa fler (visar {windowedLines.length} av {visibleLines.length})
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Light-dismiss: a click anywhere that is not a row (rows swap
                        the sheet's content) or a stacked modal/popover closes it. */}
                    <SideSheet
                        open={Boolean(selectedLine)}
                        onClose={closeDetail}
                        label="Skattekontotransaktion"
                        closeOnClickOutside
                        keepOpenWithin=".matching-list-item"
                    >
                        {selectedLine && (
                            <SkattekontoDetail
                                key={selectedLine.statement_id}
                                line={selectedLine}
                                companyId={companyId}
                                tier={tierFor(selectedLine)}
                                proposals={proposals}
                                proposalsLoading={proposalsLoading || pendingId === selectedLine.statement_id}
                                proposalsError={proposalsError}
                                linkedVerification={linkedVerification}
                                actionBusy={actionBusy}
                                actionError={actionError}
                                onLink={handleKoppla}
                                onUnlink={handleUnlink}
                                booking={booking}
                                onOpenBooking={openBooking}
                                onBookingBack={() => setBooking(null)}
                                onBookingSuccess={handleBookingSuccess}
                                onClose={closeDetail}
                                onPrev={selectedIndex > 0 ? () => stepSelection(-1) : undefined}
                                onNext={selectedIndex >= 0 && selectedIndex < windowedLines.length - 1 ? () => stepSelection(1) : undefined}
                                position={selectedIndex >= 0 ? { index: selectedIndex + 1, total: visibleLines.length } : null}
                            />
                        )}
                    </SideSheet>
                </>
            </div>

            <SkatteverketImportModal
                show={showImportModal}
                onHide={() => setShowImportModal(false)}
                onImportSuccess={handleImportSuccess}
            />
        </>
    );
}
